import { SERVICE_TYPES } from '@/core/container/types'
import type BaseModel from '@/data/models/BaseModel'
import type OrmApiRepository from '@/data/repositories/ormApiRepository'
import type ORMBaseRepository from '@/data/repositories/ormBaseRepository'
import type DeletedEntityService from '@/services/deletedEntityService'
import type DomainBaseService from '@/services/domain/domainBaseService'
import type { ModelRelationArray } from '@/services/domain/types'
import type { IFillEntitiesByIds } from '@/services/types'
import { inject, injectable } from 'inversify'

@injectable()
export class DataBulkFillerService {
  constructor(
    @inject(SERVICE_TYPES.DeletedEntityService) private readonly deletedEntityService: DeletedEntityService,
  ) {}

  private getModelName(model: typeof BaseModel) {
    return model.entity
  }
  public async fillEntitiesByIds<T extends BaseModel>(
    repo: ORMBaseRepository<T, string> & IFillEntitiesByIds<T>,
    ids: string[],
    withRelations: ModelRelationArray<T> = [],
  ) {
    const preparedIds = ids.map((t) => t.toString())
    if (!preparedIds.length) {
      return []
    }
    const modelName = this.getModelName(repo.settings().model)
    const res = await repo.fillEntitiesByIds(preparedIds, withRelations)
    const deletedEntitiesIds = preparedIds.filter((id) => {
      return res.find((t) => t.id === id) === undefined
    })

    this.deletedEntityService.markManyAsDeleted(modelName, deletedEntitiesIds)
    return res
  }

  public async findOrFillEntitiesByIds<T extends BaseModel>(
    service: DomainBaseService<OrmApiRepository<T>> & IFillEntitiesByIds<T>,
    ids: string[],
    withRelations: ModelRelationArray<T> = [],
  ) {
    const notLoadedEntitiesIds: string[] = []
    const entities: T[] = []
    const modelName = this.getModelName(service.getDomainSettings().model)
    ids.forEach((id) => {
      const entity = service.findEntityByIdOrNull(id, withRelations)
      if (entity) {
        entities.push(entity)
        return
      }
      if (!this.deletedEntityService.isDeleted(modelName, id)) {
        notLoadedEntitiesIds.push(id)
      }
    })
    const res = await service.fillEntitiesByIds(notLoadedEntitiesIds, withRelations)
    return [...entities, ...res]
  }
}
